import React, { useState } from "react";
import { Box, ClickAwayListener } from "@mui/material";
import { FaGlobe } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectCountries } from "../../store/slice/countries";
import { selectUser, updateUserData } from "../../store/slice/user";
import { selectEventsData, getEvents } from "../../store/slice/events";
import { updateLoader } from "../../store/slice/loading";
import { getCountryCategoryEvents } from "../../services/data/events";
import { changeLanguage, isRtl } from "../../i18n";
import { Style } from "./style";

// Global region + language switcher, restyled to match new-design's floating
// "globe pill" (bottom-corner, flips side under RTL). Region list is the
// real backend country list (not a hardcoded UK/IN/KW set), so it stays
// correct if the operating markets change.
const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "ar", label: "العربية" },
  { id: "hi", label: "हिन्दी" },
];

const RegionLanguagePill = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const countries = useSelector(selectCountries);
  const { countryId, language, category } = useSelector(selectUser);
  const { searchKey } = useSelector(selectEventsData);
  const rtl = isRtl(language);

  const activeCountry = countries.find((c) => c?.id === countryId);

  const handleSelectCountry = async (country) => {
    if (country?.id === countryId) return;
    localStorage.setItem("countryId", country?.id);
    dispatch(updateUserData({ countryId: country?.id }));
    try {
      dispatch(updateLoader({ loader: true }));
      const res = await getCountryCategoryEvents({
        page: 1,
        number: 12,
        country: country?.id,
        category: category?.id || 0,
        searchKey,
      });
      dispatch(getEvents(res?.data?.data));
      dispatch(updateLoader({ loader: false }));
    } catch (err) {
      dispatch(updateLoader({ loader: false }));
    }
  };

  const handleSelectLanguage = (id) => {
    if (id === language) return;
    localStorage.setItem("language", id);
    dispatch(updateUserData({ language: id }));
    changeLanguage(id);
  };

  if (!countries?.length) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={Style.wrap(rtl)}>
        {open && (
          <Box sx={Style.panel}>
            <Box sx={Style.sectionLabel}>{t("marketing.nav.region")}</Box>
            <Box sx={Style.optionsRow}>
              {countries.map((c) => (
                <Box
                  key={c.id}
                  sx={Style.option(c.id === countryId)}
                  onClick={() => handleSelectCountry(c)}
                >
                  {c.full_flag ? (
                    <Box component="img" src={c.full_flag} sx={Style.flagImg} />
                  ) : null}
                  {c?.translation?.[language]?.name || c?.translation?.en?.name}
                </Box>
              ))}
            </Box>
            <Box sx={Style.sectionLabel}>{t("marketing.nav.language")}</Box>
            <Box sx={Style.optionsRow}>
              {LANGUAGES.map((l) => (
                <Box
                  key={l.id}
                  sx={Style.option(l.id === language)}
                  onClick={() => handleSelectLanguage(l.id)}
                >
                  {l.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Box sx={Style.trigger} onClick={() => setOpen((v) => !v)}>
          <FaGlobe size={16} />
          {activeCountry?.full_flag ? (
            <Box component="img" src={activeCountry.full_flag} sx={Style.flagImg} />
          ) : null}
          <span>{language.toUpperCase()}</span>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default RegionLanguagePill;
